'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  useAddEventToFavorites,
  useEventFavoriteState,
  useRemoveEventFromFavorites,
} from '@/lib/api/event/hooks';
import { ShareButton } from '@/lib/components/Event/ShareButton';
import { StickyHeader } from '@/lib/components/StickyHeader';
import { useApiTranslation } from '@/lib/hooks/useApiTranslation';
import { useAuthSession } from '@/lib/hooks/useAuth';
import { useIsMobile } from '@/lib/hooks/useIsMobile';
import { Market } from '@/lib/types/markets/market';
import { AnimatedIcon } from '@/lib/ui-kit/animated-icon';
import { Icon } from '@/lib/ui-kit/icons';
import { UiImage } from '@/lib/ui-kit/ui-image';
import { cn } from '@/lib/ui-kit/utils';
import { EventPageHeaderMetaInfo } from './Event/EventPageHeaderMetaInfo';

type EventPageHeaderProps = {
  market: Market;
  isFavorite?: boolean;
  newsCount?: number;
};

export const EventPageHeader = ({
  market,
  isFavorite = false,
  newsCount,
}: EventPageHeaderProps) => {
  const { tr } = useApiTranslation();
  const translatedTitle = tr(market.translations, 'title', market.title);
  const isMobile = useIsMobile();

  // The page is served from the ISR cache, so `market.isFavorite` is always
  // false there — the real flag arrives from this client query.
  const { data: favoriteFromApi } = useEventFavoriteState(market.slug);
  const resolvedIsFavorite = favoriteFromApi ?? isFavorite;

  const metaInfo = (
    <EventPageHeaderMetaInfo
      volume={market.volume}
      endDate={market.endTradeDate ?? market.endDate}
      usersCount={market.participantCount}
      newsCount={newsCount ?? market.newsCount}
      ambassadorSocialLink={market.ambassadorSocialLink}
    />
  );

  const actions = (
    <div className="flex min-h-10 shrink-0 items-center gap-1">
      <FavoriteIconButton
        eventId={market.id}
        eventTitle={translatedTitle}
        initialIsFavorite={resolvedIsFavorite}
      />
      <ShareButton
        size={16}
        title={translatedTitle}
        className="flex size-8 shrink-0 items-center justify-center rounded-[8px] text-v2-icon-primary transition-colors hover:bg-v2-btn-secondary"
      />
    </div>
  );

  // On mobile the header reflows to a compact layout (40px image, 15px title,
  // actions on the title row, counters wrapping full-width below) and renders
  // as a plain auto-height block so long, multi-line titles are never clipped
  // by the fixed 96px sticky box. The sticky scroll-shrink stays desktop-only.
  if (isMobile) {
    return (
      <div className="flex flex-col gap-4 bg-v2-surface-primary py-2">
        <div className="flex items-start gap-2">
          <UiImage
            size={40}
            src={market.image || '/evfallback.webp'}
            fallback="/evfallback.webp"
            alt={`${translatedTitle} image`}
            className="shrink-0 rounded-[8px] border border-v2-border"
          />
          <h1 className="flex min-h-10 min-w-0 flex-1 items-center text-[15px] leading-[20px] font-semibold text-v2-text-primary">
            {translatedTitle}
          </h1>
          {actions}
        </div>
        {metaInfo}
      </div>
    );
  }

  return (
    <StickyHeader
      startHeight={96}
      endHeight={96}
      minScroll={0}
      maxScroll={100}
      // Same frosted fill as the app header (HeaderV2Container): on ::before, so
      // backdrop-filter doesn't trap position:fixed descendants inside the header.
      className="before:pointer-events-none before:absolute before:inset-0 before:-z-10 before:bg-v2-surface-primary/80 before:backdrop-blur-[16px] before:transition-colors before:duration-300"
    >
      <div className="relative flex h-full items-center gap-4">
        <div
          className="flex min-w-0 flex-1 origin-left items-start gap-4"
          style={{
            transform: 'scale( calc(1 * (1 - var(--scroll-value)) + 0.75 * var(--scroll-value)) )',
          }}
        >
          <UiImage
            size={64}
            src={market.image || '/evfallback.webp'}
            fallback="/evfallback.webp"
            alt={`${translatedTitle} image`}
            className="shrink-0 rounded-[8px] border border-v2-border"
          />

          <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
            <h1 className="min-w-0 text-[24px] leading-[32px] font-medium text-v2-text-primary">
              {translatedTitle}
            </h1>
            {metaInfo}
          </div>
        </div>

        {actions}

        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 border-b border-v2-border"
          style={{ opacity: 'calc(var(--scroll-value))' }}
        />
      </div>
    </StickyHeader>
  );
};

interface FavoriteIconButtonProps {
  eventId: string;
  eventTitle: string;
  initialIsFavorite: boolean;
}

function FavoriteIconButton({ eventId, eventTitle, initialIsFavorite }: FavoriteIconButtonProps) {
  const t = useTranslations('event');
  const { user } = useAuthSession();
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);

  useEffect(() => {
    setIsFavorite(initialIsFavorite);
  }, [initialIsFavorite]);

  const addToFavorites = useAddEventToFavorites();
  const removeFromFavorites = useRemoveEventFromFavorites();
  const isLoading = addToFavorites.isPending || removeFromFavorites.isPending;

  const shortTitle = eventTitle.length > 30 ? `${eventTitle.slice(0, 30).trim()}...` : eventTitle;

  const handleToggle = () => {
    if (!user) {
      toast.error(t('loginToFavorite'));
      return;
    }
    const wasFavorite = isFavorite;
    setIsFavorite(!wasFavorite);

    const mutation = wasFavorite ? removeFromFavorites : addToFavorites;
    const errorKey = wasFavorite ? 'favoriteRemoveError' : 'favoriteAddError';
    const successKey = wasFavorite ? 'favoriteRemoved' : 'favoriteAdded';

    mutation.mutate(eventId, {
      onError: () => {
        setIsFavorite(wasFavorite);
        toast.error(t(errorKey));
      },
      onSuccess: () => {
        toast.success(t(successKey, { title: shortTitle }));
      },
    });
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={isLoading}
      aria-label={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
      aria-pressed={isFavorite || undefined}
      className={cn(
        'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[8px] text-v2-icon-primary outline-none',
        'transition-colors hover:bg-v2-btn-secondary hover:text-v2-icon-primary-hover',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {isFavorite ? (
        <Icon name="BookmarkFilled" size={16} />
      ) : (
        <AnimatedIcon name="bookmark" size={16} className="lottie-current" />
      )}
    </button>
  );
}
